import { useMemo } from "react";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { DASHBOARD_COLORS } from "@/lib/dashboardColors";
import { formatCurrency } from "@/utils/format";

export default function TaxBreakdownChart({ taxEstimate, isLoading, error }) {
  const data = useMemo(() => {
    if (!taxEstimate) return [];
    return [
      { name: "ESt", value: taxEstimate.income_tax || 0, color: DASHBOARD_COLORS.revenue },
      { name: "USt", value: taxEstimate.vat || 0, color: DASHBOARD_COLORS.revenueLight },
      { name: "SV", value: taxEstimate.social_security || 0, color: DASHBOARD_COLORS.expenses },
    ].filter((item) => item.value > 0);
  }, [taxEstimate]);

  const totalValue = useMemo(
    () => data.reduce((sum, item) => sum + item.value, 0),
    [data]
  );

  const legendItems = useMemo(() => data.map((item) => ({ name: item.name, color: item.color })), [data]);

  return (
    <Card data-testid="tax-breakdown-chart" className="h-full">
      <CardContent className="relative h-full min-h-[320px] p-0">
        <div className="absolute left-6 top-4 text-base font-semibold text-slate-900">Steuer-Aufteilung</div>
        <div
          className="absolute left-6 top-10 text-xs text-slate-500"
          data-testid="tax-breakdown-hint"
        >
          Voraussichtliche Steuerlast
        </div>
        {isLoading && <Skeleton className="h-full w-full" data-testid="tax-breakdown-loading" />}
        {!isLoading && error && (
          <div className="text-sm text-red-600" data-testid="tax-breakdown-error">
            Steuerdaten konnten nicht geladen werden.
          </div>
        )}
        {!isLoading && !error && data.length === 0 && (
          <div className="text-sm text-gray-500" data-testid="tax-breakdown-empty">
            Keine Steuerdaten im Zeitraum.
          </div>
        )}
        {!isLoading && !error && data.length > 0 && (
          <>
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" data-testid="tax-breakdown-donut">
              <div className="h-64 w-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={data}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={70}
                      outerRadius={115}
                      cx="50%"
                      cy="50%"
                      paddingAngle={1}
                    >
                      {data.map((entry) => (
                        <Cell key={entry.name} fill={entry.color} />
                      ))}
                    </Pie>
                    <text
                      x="50%"
                      y="48%"
                      textAnchor="middle"
                      dominantBaseline="middle"
                      className="fill-slate-700 text-sm font-semibold"
                    >
                      {formatCurrency(totalValue)}
                    </text>
                    <text
                      x="50%"
                      y="56%"
                      textAnchor="middle"
                      dominantBaseline="middle"
                      className="fill-slate-400 text-[11px]"
                    >
                      Gesamtsteuer
                    </text>
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div
              className="absolute bottom-6 left-1/2 flex -translate-x-1/2 items-center gap-4 text-xs text-slate-500"
              data-testid="tax-breakdown-legend"
            >
              {legendItems.map((item) => (
                <div key={item.name} className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full" style={{ backgroundColor: item.color }} />
                  {item.name}
                </div>
              ))}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}